import { useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Button,
  Col,
  Container,
  Modal,
  Nav,
  Navbar,
  Offcanvas,
  Row
} from 'react-bootstrap'
import parse from 'html-react-parser'
import { useMediaQuery } from 'react-responsive'

import { navigateTo, menu } from '../index'
import {
  ROUTE_LOGIN,
  ROUTE_LOGOUT,
  ROUTE_PAYMENTS_COMPLETE,
  ROUTE_SIGNUP,
  ROUTE_VERIFY,
  ROUTE_WELCOME,
  ROUTE_PROFILE,
  ROUTE_DAILY_REMBLE,
  ROUTE_FORGOT_PASSWORD,
  getSegment,
  getSegments,
  ROUTE_LOGIN_PRIVATE,
  ROUTE_LOGIN_OAUTH,
  ROUTE_TOS_ACCEPTANCE,
  ROUTE_ASSESSMENT,
  ROUTE_ASSESSMENT_COMPLETE,
  ROUTE_ACTIVITIES_ALL,
  ROUTE_TODAY,
  ROUTE_COURSES_ALL,
  ROUTE_SEARCH,
  ROUTE_ACTIVITIES_AFFIRMATIONS_ALL,
  ROUTE_ACTIVITIES_BREATHING_ALL,
  ROUTE_ACTIVITIES_COPINGSKILLS_ALL,
  ROUTE_ACTIVITIES_MEDITATION_ALL,
  ROUTE_ACTIVITIES_JOURNALING_ALL,
  ROUTE_ACTIVITIES_RELATIONSHIPQA_ALL,
  ROUTE_ACTIVITIES_COMPLIMENTS_ALL,
  ROUTE_ACTIVITIES_DATE_IDEAS_ALL,
  ROUTE_CHAT_GENERAL,
  ROUTE_LOGIN_OAUTH_OPENID,
  ROUTE_CATEGORY,
  KEY_CATEGORY,
  ROUTE_LIBRARY
} from '../../helpers/Paths'
import AccountMenu from '../AccountMenu'
import './Header.scss'
import { toggleTabBar } from 'components/common/helpers/Mobile'
import Search from 'components/search/Search/Search'

export const unlockBodyPosition = () => {
  // Reset the body position
  const scrollY = document.body.style.top
  document.body.style.position = 'relative'
  document.body.style.top = null
  document.body.style.width = null
  window.scrollTo(0, parseInt(scrollY || '0') * -1)

  return true
}

const getIntroCover = () => document?.querySelector('.intro-cover')

/**
 * Global Header component for displaying the app header
 * @param {Object} paramObj Dependency object, used for initializing the header
 */
export default function Header({ title, returnPath }) {
  const history = useHistory()
  const { pathname, search } = useLocation()

  const [backButtonPath, setBackButtonPath] = useState(returnPath)
  const [navbarExpanded, setNavbarExpanded] = useState()
  const [showSearchModal, setShowSearchModal] = useState(false)
  const headerRef = useRef()
  const isLargeScreen = useMediaQuery({ minWidth: 768 })

  /**
   * Helper function to hide the header completely on certain URLs
   * @returns Boolean indicating whether or not to hide the header
   */
  const shouldHideHeader = () => {
    // Hide nav on payments and on focus screens
    const focusScreens = [
      '/', // Hide on / webroot
      ROUTE_LOGIN,
      ROUTE_LOGIN_PRIVATE,
      ROUTE_LOGIN_OAUTH,
      ROUTE_LOGIN_OAUTH_OPENID,
      ROUTE_LOGOUT,
      ROUTE_PAYMENTS_COMPLETE,
      ROUTE_SIGNUP,
      ROUTE_WELCOME,
      ROUTE_TOS_ACCEPTANCE,
      ROUTE_VERIFY,
      ROUTE_FORGOT_PASSWORD,
      ROUTE_ASSESSMENT,
      ROUTE_ASSESSMENT_COMPLETE
    ]

    // Don't display header if user is on a "Focus" screen
    return focusScreens.includes(pathname)
  }

  const noBgHeader = () => {
    const urls = [
      ROUTE_LIBRARY
    ]

    return urls.some(v => pathname.includes(v))
  }

  const newLayout = () => {
    const urls = [
      ROUTE_CATEGORY.replace(`/:${KEY_CATEGORY}`, ''),
    ]

    return urls.some(v => pathname.includes(v))
  }

  /**
   * Helper function to show the search feature on certain URLs
   * @returns Boolean indicating whether or not to show the search feature
   */
  const shouldShowSearch = () => {
    // Show search on all landing screens
    const landingScreens = [
      ROUTE_ACTIVITIES_ALL,
      ROUTE_TODAY,
      ROUTE_COURSES_ALL,
      ROUTE_SEARCH,
      ROUTE_ACTIVITIES_AFFIRMATIONS_ALL,
      ROUTE_ACTIVITIES_BREATHING_ALL,
      ROUTE_ACTIVITIES_COPINGSKILLS_ALL,
      ROUTE_ACTIVITIES_MEDITATION_ALL,
      ROUTE_ACTIVITIES_JOURNALING_ALL,
      ROUTE_ACTIVITIES_RELATIONSHIPQA_ALL,
      ROUTE_ACTIVITIES_COMPLIMENTS_ALL,
      ROUTE_ACTIVITIES_DATE_IDEAS_ALL
    ]

    // Show search if user is on a "Landing" screen or if they are on any chat screen
    return (
      landingScreens.includes(pathname) || pathname.includes(ROUTE_CHAT_GENERAL)
    )
  }


  const lockBodyPosition = () => {
    // Lock the document body in place
    const windowScrollY = window.scrollY
    document.body.style.top = `-${windowScrollY}px`
    document.body.style.position = 'fixed'
    document.body.style.width = '100%'

    return true
  }

  // add class to header when on a specific practice page
  useEffect(() => {
    const practiceType = getSegment(1)
    const headerClassList = headerRef.current?.classList
    const match = headerRef.current?.className.match(/header-\S*/)
    const regexNumStr = pathname?.match(/\d+\/\w+/) // get [11015/breath/] pattern from URL
    const className =
      regexNumStr !== null ? regexNumStr[0].split('/').pop() : practiceType

    const removeHeaderClass = () =>
      headerClassList ? headerClassList.remove(match[0]) : null

    const addHeaderClass = segment => {
      if (getSegments().length >= segment) {
        match && removeHeaderClass()

        // Add the class to the header if it's available
        if (headerClassList) headerClassList.add(`header-${className}`)
      }
    }

    if (getSegment(0) === 'practices') {
      if (getSegment(2) === 'meditate') {
        headerClassList.add('header-meditate')
        return
      }

      addHeaderClass(3)

      if (getSegments().length <= 2) {
        match && removeHeaderClass()
      }

      addHeaderClass(4)
    }

    getSegment(0) === 'today' && match && removeHeaderClass() // show header after return back to "Today" from Daily Remble
    getSegment(1) === 'daily-remble' && addHeaderClass(2) // hide header on Daily Remble because of bookmark issue

    headerRef.current?.classList?.toggle(
      'header-today',
      getSegment(0) === 'today' && pathname.substring(1) === 'today'
    )
  }, [pathname, isLargeScreen])

  useEffect(() => {
    let path = returnPath

    // Do we have a query string?
    if (search) {
      // Override the path
      const params = new URLSearchParams(search)
      const passedReturnPath = params.get('returnPath')

      // If we have a custom return path in the query string, let's use it
      if (passedReturnPath) path = passedReturnPath
    }

    // Is our path the same as the current url? Clear it out
    if (path === pathname) path = null

    // Engage all the dark mode magic
    document.querySelectorAll('.toggle-lights').forEach(el => {
      // Toggle the icon, if we're already in dark mode
      if (document.body.classList.contains('dark')) {
        el.classList.remove('bi-sunset')
        el.classList.add('bi-sunrise')
      }

      el.addEventListener('click', () => {
        el.classList.toggle('bi-sunrise')
        el.classList.toggle('bi-sunset')
        document.body.classList.toggle('dark')
      })
    })

    // Update our back button
    setBackButtonPath(path)

    // Update the header transparency
    headerRef.current?.classList?.toggle(
      'transparent',
      pathname.includes(ROUTE_DAILY_REMBLE)
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, returnPath, backButtonPath, search])

  // scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const introCoverHeight = getIntroCover()?.offsetHeight

      headerRef.current?.classList?.toggle(
        'scroll-transparent',
        currentScrollY > (introCoverHeight ?? 60) - 40 // 40 is content card padding
      )
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => window.removeEventListener('scroll', handleScroll)
  }, [pathname])

  if (shouldHideHeader()) return null

  return (
    <>
      <header ref={headerRef} className={`header fixed-top ${noBgHeader() ? 'no-bg' : ''}`}>
        <Navbar
          expand={false}
          expanded={navbarExpanded}
          onToggle={expanded => {
            setNavbarExpanded(expanded)
          }}
          className='mobile-menu'
        >
          <Row className='w-100'>
            <Col xs={newLayout() ? 2 : 4 }>
              {backButtonPath ? (
                <span
                  onClick={() => {
                    // Turn the tab bar back on. This is a hack to get around the fact that
                    // the tab bar is hidden on the checkout pages, but we want to show it when
                    // the user clicks the back button.
                    toggleTabBar('on')
                    navigateTo(backButtonPath, history)
                  }}
                  className='nav-icon-link left desktop-hidden'
                >
                  <i className='icon bi-arrow-left'></i>{' '}
                </span>
              ) : (
                <Navbar.Toggle
                  className='bg-white profile-link'
                  aria-controls={`offcanvasNavbar`}
                >
                  <i className='icon bi-person-circle'></i>
                  <i className='icon bi-list'></i>
                </Navbar.Toggle>
              )}
            </Col>
            <Col xs={newLayout() ? 10 : 6} className='text-center px-0'>
              {title && (
                <Navbar.Text as='div' className={newLayout() ? 'text-left title' : 'text-center title'}>
                  {parse(title)}
                </Navbar.Text>
              )}
            </Col>
            {!newLayout() && (
              <Col xs={2} className='text-right px-0'>
                {shouldShowSearch() && (
                  <Button
                    id='header-search-button'
                    variant='text-link'
                    onClick={_ => lockBodyPosition() && setShowSearchModal(true)}
                  >
                    <i className='bi bi-search' />
                  </Button>
                )}
              </Col>
            )}
          </Row>

          <Navbar.Offcanvas
            id={`offcanvasNavbar`}
            aria-labelledby={`offcanvasNavbarLabel`}
            placement='start'
          >
            <Offcanvas.Header closeButton>
              <Offcanvas.Title id={`offcanvasNavbarLabel`}>
                Account
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <AccountMenu setNavbarExpanded={setNavbarExpanded} />
            </Offcanvas.Body>
          </Navbar.Offcanvas>
        </Navbar>

        <Nav className='w-100'>
          {/* Desktop menu is persistent, regardless of backButtonPath, etc. */}
          <Container className='desktop-menu'>
            <Row>
              <Col md={4} className='brand-container'>
                {backButtonPath ? (
                  <span
                    onClick={() => navigateTo(backButtonPath, history)}
                    className='nav-icon-link left'
                  >
                    <i className='icon bi-arrow-left'></i>{' '}
                    <span className='mobile-hidden nav-icon-link-text'>
                      Back
                    </span>
                  </span>
                ) : (
                  <Nav.Link
                    className='profile-link'
                    onClick={_ => navigateTo(ROUTE_PROFILE, history)}
                  >
                    {/* Profile link */}
                    <span className='icon'>
                      <i className='icon bi-person-circle'></i>
                    </span>
                    <small className='label'>Account</small>
                  </Nav.Link>
                )}
              </Col>
              <Col md={8} className='text-right'>
                {menu().map(tab => (
                  <Nav.Link
                    key={tab.route}
                    onClick={_ => navigateTo(tab.route, history)}
                    className={`${
                      pathname.indexOf(tab.route) > -1 ? 'active' : ''
                    }`}
                  >
                    <span className='icon'>{tab.icon}</span>
                    <small className='label'>{tab.label}</small>
                  </Nav.Link>
                ))}
              </Col>
            </Row>
          </Container>
        </Nav>
      </header>

      <Modal
        show={showSearchModal}
        onHide={_ => unlockBodyPosition() && setShowSearchModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Search</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Search
            hideHandler={_ => unlockBodyPosition() && setShowSearchModal(false)}
          />
        </Modal.Body>
      </Modal>
    </>
  )
}
